import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import snapmenuLogo from '../../assets/snapmenu.png';
import { API } from '../../config/envConfig';

export const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    companyName: '',
    contactAddress: '',
    comments: ''
  });
  const [menuEnrichmentEnabled, setMenuEnrichmentEnabled] = useState(true);
  const [expandedSection, setExpandedSection] = useState<'food' | 'drinks'>('food');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const generateRandomPassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+-=[]{}|;:,.<>?';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsLoading(true);
    setError('');
    
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        contactPhone: formData.phone,
        companyName: formData.companyName,
        number_of_merchants: 0,
        contactAddress: formData.contactAddress,
        comments: formData.comments,
        password: generateRandomPassword(),
        isoLicense: '',
        selected_features: menuEnrichmentEnabled ? ['developer_docs', 'enrich_menu'] : ['developer_docs'],
        business_type: ['Restaurant']
      };

      const response = await fetch(API.CREATE_ISO, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Signup failed');
      }

      const data = await response.json();
      console.log('Signup successful:', data);
      
      setSuccess(true);
      
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      console.error('Signup error:', error);
      setError(error instanceof Error ? error.message : 'Signup failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #f0f4f8 0%, #d9e2ec 100%)' }}>
      <div className="max-w-5xl w-full mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src={snapmenuLogo} alt="SnapMenu AI Logo" className="mx-auto h-12 w-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
            Unlock Your Restaurant's Full Potential
          </h1>
          <p className="mt-2 text-base text-gray-600">
            Revolutionize Your Menu with AI-Powered Enrichment
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white p-8 rounded-lg shadow-lg w-full">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Your Details */}
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">Your Details</h2>
                
                {/* Name and Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      placeholder="Enter full name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-gray-50 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder="Enter your company's email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-gray-50 text-sm"
                    />
                  </div>
                </div>

                {/* Company Name and Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      placeholder="Your company name"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-gray-50 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Enter your company's phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-gray-50 text-sm"
                    />
                  </div>
                </div>

                {/* Contact Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Contact Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="contactAddress"
                    placeholder="Enter your company's address"
                    value={formData.contactAddress}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-gray-50 text-sm resize-none"
                  />
                </div>

                {/* Comments */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Comments
                  </label>
                  <textarea
                    name="comments"
                    placeholder="Any additional comments or questions"
                    value={formData.comments}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-gray-50 text-sm resize-none"
                  />
                </div>
              </div>

              {/* Right Column - Menu Enrichment Package */}
              <div className="relative h-full">
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-red-500 to-fuchsia-600 text-white text-sm font-bold px-5 py-2 rounded-full shadow-lg z-10">
                  ✨ Most Popular
                </span>
                <div className="bg-gradient-to-br from-violet-100 to-red-100 dark:from-violet-900/50 dark:to-red-900/20 p-6 rounded-lg border border-violet-200 dark:border-violet-700/50 pt-8 h-full flex flex-col">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Menu Enrichment</h3>
                      <p className="text-xs text-gray-700 mt-1">Additional cost applicable</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setMenuEnrichmentEnabled(!menuEnrichmentEnabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        menuEnrichmentEnabled ? 'bg-gradient-to-r from-red-500 to-fuchsia-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          menuEnrichmentEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="space-y-4 text-sm flex-1 overflow-y-auto max-h-[600px]">
                    {/* Food Items Enrichment */}
                    <div className="bg-white/50 rounded-lg border border-violet-200">
                      <button
                        type="button"
                        onClick={() => setExpandedSection('food')}
                        className="w-full p-4 flex justify-between items-center hover:bg-white/30 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xl">🍽️</span>
                          <h4 className="font-semibold text-gray-900 text-sm">Food Items Enrichment</h4>
                        </div>
                        <span className={`text-gray-600 transition-transform ${expandedSection === 'food' ? 'rotate-180' : ''}`}>
                          ▼
                        </span>
                      </button>
                      {expandedSection === 'food' && (
                        <ul className="space-y-2 text-xs px-4 pb-4 min-h-[280px]">
                        <li className="pl-3 border-l-2 border-red-500">
                          <span className="font-medium text-gray-900">Product Description:</span>
                          <span className="text-gray-700"> Short summary (max 200 characters)</span>
                        </li>
                        <li className="pl-3 border-l-2 border-red-500">
                          <span className="font-medium text-gray-900">Appearance:</span>
                          <span className="text-gray-700"> Visual description (max 100 characters)</span>
                        </li>
                        <li className="pl-3 border-l-2 border-red-500">
                          <span className="font-medium text-gray-900">Ingredients:</span>
                          <span className="text-gray-700"> Key ingredients list (max 100 characters)</span>
                        </li>
                        <li className="pl-3 border-l-2 border-red-500">
                          <span className="font-medium text-gray-900">Serving:</span>
                          <span className="text-gray-700"> Best accompaniments (max 100 characters)</span>
                        </li>
                        <li className="pl-3 border-l-2 border-red-500">
                          <span className="font-medium text-gray-900">Flavors:</span>
                          <span className="text-gray-700"> Main flavor notes (max 100 characters)</span>
                        </li>
                        <li className="pl-3 border-l-2 border-red-500">
                          <span className="font-medium text-gray-900">Variations:</span>
                          <span className="text-gray-700"> Common modifications (max 100 characters)</span>
                        </li>
                        <li className="pl-3 border-l-2 border-red-500">
                          <span className="font-medium text-gray-900">Allergy Info:</span>
                          <span className="text-gray-700"> Allergens & dietary concerns (max 100 characters)</span>
                        </li>
                        <li className="pl-3 border-l-2 border-red-500">
                          <span className="font-medium text-gray-900">Nutrients & Calories:</span>
                          <span className="text-gray-700"> Key nutrients & approximate calories per serving</span>
                        </li>
                        <li className="pl-3 border-l-2 border-red-500">
                          <span className="font-medium text-gray-900">Dietary Tags:</span>
                          <span className="text-gray-700"> Vegan status, food type (veg/non-veg), meal time</span>
                        </li>
                        <li className="pl-3 border-l-2 border-red-500">
                          <span className="font-medium text-gray-900">Categories & Metadata:</span>
                          <span className="text-gray-700"> Level 1/2 categories, cooking method, spice applicability</span>
                        </li>
                        </ul>
                      )}
                    </div>

                    {/* Drinks Menu Enrichment */}
                    <div className="bg-white/50 rounded-lg border border-violet-200">
                      <button
                        type="button"
                        onClick={() => setExpandedSection('drinks')}
                        className="w-full p-4 flex justify-between items-center hover:bg-white/30 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xl">🍹</span>
                          <h4 className="font-semibold text-gray-900 text-sm">Drinks Menu Enrichment</h4>
                        </div>
                        <span className={`text-gray-600 transition-transform ${expandedSection === 'drinks' ? 'rotate-180' : ''}`}>
                          ▼
                        </span>
                      </button>
                      {expandedSection === 'drinks' && (
                        <ul className="space-y-2 text-xs px-4 pb-4 min-h-[280px]">
                        <li className="pl-3 border-l-2 border-fuchsia-600">
                          <span className="font-medium text-gray-900">Brand/Distillery:</span>
                          <span className="text-gray-700"> Producer name & company info</span>
                        </li>
                        <li className="pl-3 border-l-2 border-fuchsia-600">
                          <span className="font-medium text-gray-900">Type & Sub-Type:</span>
                          <span className="text-gray-700"> Category (Beer, Wine, Whiskey) & specific style (IPA, Merlot)</span>
                        </li>
                        <li className="pl-3 border-l-2 border-fuchsia-600">
                          <span className="font-medium text-gray-900">ABV & Volume:</span>
                          <span className="text-gray-700"> Alcohol by volume (%) & serving size (ml/oz)</span>
                        </li>
                        <li className="pl-3 border-l-2 border-fuchsia-600">
                          <span className="font-medium text-gray-900">Origin & Region:</span>
                          <span className="text-gray-700"> Country of origin & specific region/appellation</span>
                        </li>
                        <li className="pl-3 border-l-2 border-fuchsia-600">
                          <span className="font-medium text-gray-900">Vintage & Age:</span>
                          <span className="text-gray-700"> Vintage year (wines) & age statement (spirits)</span>
                        </li>
                        <li className="pl-3 border-l-2 border-fuchsia-600">
                          <span className="font-medium text-gray-900">Tasting Profile:</span>
                          <span className="text-gray-700"> Descriptive attributes, tasting notes, flavor impressions</span>
                        </li>
                        <li className="pl-3 border-l-2 border-fuchsia-600">
                          <span className="font-medium text-gray-900">Sensory Details:</span>
                          <span className="text-gray-700"> Color, aroma notes, mouthfeel texture</span>
                        </li>
                        <li className="pl-3 border-l-2 border-fuchsia-600">
                          <span className="font-medium text-gray-900">Finish:</span>
                          <span className="text-gray-700"> Palate ending (short, warming, lingering)</span>
                        </li>
                        <li className="pl-3 border-l-2 border-fuchsia-600">
                          <span className="font-medium text-gray-900">Product Description:</span>
                          <span className="text-gray-700"> Comprehensive description (100 words)</span>
                        </li>
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Success and Error Messages */}
            {success && (
              <div className="mt-6 p-4 rounded-lg bg-green-50 border border-green-500">
                <p className="text-sm font-medium text-green-600">
                  ✓ Signup successful! Redirecting to login page...
                </p>
              </div>
            )}

            {error && (
              <div className="mt-6 p-4 rounded-lg bg-red-50 border border-red-500">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <div className="mt-8">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 rounded-md text-white font-medium text-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed bg-red-500 hover:bg-red-600"
              >
                {isLoading ? 'Signing Up...' : 'Sign Up'}
              </button>
            </div>
          </form>

          {/* Sign In Link */}
          <div className="text-center mt-6">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium text-red-500 hover:text-red-600"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
